import React, { useMemo } from 'react';
import {
  CarrierConfigIcon,
  ClaimsIcon,
  ContributionsMenuIcon,
  DistributorIcon,
  EmployeesIcon,
  EmployerIcon,
  FolderIcon,
  HomeIcon,
  NewPlansIcon,
  PartnerIcon,
  ProcessingDefinitionIcon,
  QuickLinksIcon,
  ReconciliationReportsIcon,
  ReportsIcon,
  RolesAndSecurityIcon,
  SubsidiariesIcon,
} from '@common-fe/common-fe';

import { OrganizationTypes } from '@/common/constants';
import Permissions from '@/common/permissions';
import ROUTES from '@/common/routes';
import { useCurrentOrganization, useHasAccess, useHistory } from '@/modules/core/hooks';
import { useOrganizationOverridableProperties } from '@/modules/core/hooks/useOrganizationOverridableProperties';
import { usePlansAccess } from '@/modules/plan/hooks/usePlansAccess';
import { useAuthStore } from '@/modules/user/stores';

import { SidebarItemProps } from '../SidebarItem';

export const useSidebarOptions = (): SidebarItemProps[] => {
  const {
    isObservingModeActive,
    observingOrganization: { type },
  } = useCurrentOrganization();
  const { user, auth } = useAuthStore();

  const { observingOrganizationData } = useOrganizationOverridableProperties();

  const { getPath } = useHistory();

  const isSystem = useMemo(() => type === OrganizationTypes.system, [type]);

  const isEmployer = useMemo(
    () => type === OrganizationTypes.employer || type === OrganizationTypes.company,
    [type]
  );

  const isRolesAndSecurityAvailable = useHasAccess([{ permission: Permissions.VIEW_ROLES_AND_SECURITY }]);
  const isReportsAvailable = useHasAccess([{ permission: Permissions.REPORTS }]);
  const isFileManagerEnabled = useMemo(
    () => observingOrganizationData?.isFileManagerAccessEnabled,
    [observingOrganizationData]
  );

  const isEmployee = useMemo(() => type === OrganizationTypes.employee, [type]);
  const isSubsidiary = useMemo(() => type === OrganizationTypes.subsidiary, [type]);
  const isSubgroup = useMemo(() => type === OrganizationTypes.subgroup, [type]);
  const isDistributor = useMemo(() => type === OrganizationTypes.distributor, [type]);

  const isContributionsAccessibleObserving = useMemo(
    () => isEmployer || isSubsidiary,
    [isEmployer, isSubsidiary]
  );

  const isContributionsAccessible = useHasAccess([{ permission: Permissions.VIEW_CONTRIBUTIONS }]);

  const isPartnersOptionAccessible = useHasAccess([
    { organizationType: OrganizationTypes.system },
    { organizationType: OrganizationTypes.distributor },
  ]);
  const isHealthPlanHubAccessible = useHasAccess([
    { permission: Permissions.VIEW_HEALTH_PLAN },
    { permission: Permissions.CREATE_CARRIER_CONFIG },
    { permission: Permissions.VIEW_COPAY },
    { permission: Permissions.DISABLE_CARRIER_CONFIG },
    { permission: Permissions.SET_UP_HEALTH_PLAN },
  ]);
  const isHealthPlansAccessible = useHasAccess([{ permission: Permissions.VIEW_HEALTH_PLAN }]);
  const isSetUpPartnerOptionAccessible = useHasAccess([
    {
      organizationType: OrganizationTypes.system,
      permission: Permissions.EDIT_ER_DATA,
    },
    {
      organizationType: OrganizationTypes.distributor,
      permission: Permissions.EDIT_ER_DATA,
    },
  ]);

  const isSetUpDistributorOptionAccessible = useHasAccess([
    {
      organizationType: OrganizationTypes.system,
      permission: Permissions.EDIT_ER_DATA,
    },
    {
      organizationType: OrganizationTypes.partner,
      permission: Permissions.EDIT_ER_DATA,
    },
  ]);

  const isPlansShowed = usePlansAccess();
  const isViewCopayGroups = useHasAccess([
    {
      permission: Permissions.VIEW_COPAY,
    },
    {
      permission: Permissions.VIEW_ALL,
    },
  ]);
  const isEmployersOptionAccessible = useHasAccess([
    { organizationType: OrganizationTypes.system },
    { organizationType: OrganizationTypes.distributor },
    { organizationType: OrganizationTypes.partner },
  ]);

  const isSetUpEmployerOptionAccessible = useHasAccess([
    {
      organizationType: OrganizationTypes.system,
      permission: Permissions.EDIT_ER_DATA,
    },
    {
      organizationType: OrganizationTypes.distributor,
      permission: Permissions.EDIT_ER_DATA,
    },
    {
      organizationType: OrganizationTypes.partner,
      permission: Permissions.EDIT_ER_DATA,
    },
  ]);
  const isFinOpsShowed = useHasAccess([
    { permission: Permissions.VIEW_LOCKBOX },
    { permission: Permissions.VIEW_FINOPS_CHECKS },
    { permission: Permissions.VIEW_INVOICES },
    { permission: Permissions.BANK_OPS },
  ]);
  const isClaimsButtonAvailable = useHasAccess([
    { permission: Permissions.VIEW_CLAIM },
    { permission: Permissions.VIEW_APPEAL },
    { permission: Permissions.PROCESS_APPEAL },
  ]);
  const isFileManagerAccessible = useHasAccess([{ permission: Permissions.VIEW_FILE_REPORTS }]);
  const isProcessingDefinitionAccessible = useHasAccess([
    { permission: Permissions.VIEW_PROCESSING_DEFINITION },
    { permission: Permissions.MANAGE_SERVICES },
    { permission: Permissions.MANAGE_PAYOUT_DEFINITION },
    { permission: Permissions.SET_UP_PROCESSING_DEFINITION },
  ]);
  const isServicesAccessible = useHasAccess([
    { permission: Permissions.MANAGE_SERVICES },
    { permission: Permissions.SET_UP_PROCESSING_DEFINITION },
  ]);
  const isPayoutDefinitionsAccessible = useHasAccess([
    { permission: Permissions.SET_UP_PROCESSING_DEFINITION },
    { permission: Permissions.MANAGE_PAYOUT_DEFINITION },
  ]);

  const hasPermissionToViewProfiles = useHasAccess([{ permission: Permissions.VIEW_PROFILE }]);

  const allOptions = useMemo<SidebarItemProps[]>(
    () => [
      {
        key: 'home',
        title: 'Home',
        isNewBadge: isEmployer || isSubsidiary,
        icon: <HomeIcon color="navigationIconDefault" />,
        url: ROUTES.HOME,
      },
      {
        key: 'partners',
        title: 'Partners',
        icon: <PartnerIcon color="navigationIconDefault" />,
        url: ROUTES.PARTNERS,
        hide: !isPartnersOptionAccessible || !isSystem,
      },
      {
        key: 'distributors',
        title: 'Distributors',
        icon: <DistributorIcon color="navigationIconDefault" />,
        url: ROUTES.DISTRIBUTORS,
        hide: isDistributor || isEmployer || isEmployee || isSubsidiary || isSubgroup,
      },

      {
        key: 'employers',
        title: 'Employers',
        icon: <EmployerIcon color="navigationIconDefault" />,
        url: ROUTES.EMPLOYERS,
        hide: !isEmployersOptionAccessible || isEmployer || isSubsidiary || isSubgroup,
      },

      {
        key: 'subsidiaries',
        title: 'Subsidiaries',
        icon: <SubsidiariesIcon color="navigationIconDefault" />,
        url: ROUTES.SUBSIDIARIES,
        hide: isSubsidiary || isSubgroup || !isEmployer,
      },
      ...(hasPermissionToViewProfiles
        ? [
            {
              key: 'employees',
              title: 'Employees',
              icon: <EmployeesIcon color="navigationIconDefault" />,
              url: ROUTES.EMPLOYEES,
              subUrls: [ROUTES.EMPLOYEE],
              hide: isSubgroup,
            },
          ]
        : []),
      {
        key: 'plans',
        title: 'Plans',
        icon: <NewPlansIcon color="navigationIconDefault" />,
        url: ROUTES.PLANS,
        subUrls: [ROUTES.TEMPLATE_VIEW, ROUTES.TEMPLATES],
        hide: !isPlansShowed || isSubgroup,
      },
      {
        key: 'processingDefinition',
        title: 'Processing Definition',
        icon: <ProcessingDefinitionIcon color="navigationIconDefault" />,
        url: ROUTES.PROCESSING_DEFINITION,
        options: [
          {
            key: 'enrollment_matrix',
            title: 'Enrollment Matrix',
            url: ROUTES.ENROLLMENT_MATRIX,
          },
          {
            key: 'processing_rules',
            title: 'Processing Rules',
            url: ROUTES.PROCESSING_RULES,
          },
          ...(isPayoutDefinitionsAccessible
            ? [
                {
                  key: 'payout_definitions',
                  title: 'Payout Definitions',
                  url: ROUTES.PAYOUT_DEFINITIONS,
                },
              ]
            : []),
          ...(isServicesAccessible
            ? [
                {
                  key: 'services',
                  title: 'Services',
                  url: ROUTES.SERVICES,
                },
              ]
            : []),
        ],
        hide: !isProcessingDefinitionAccessible || isSubsidiary,
      },
      ...(isHealthPlanHubAccessible
        ? [
            {
              key: 'healthPlanHub',
              title: 'Health Plan Hub',
              icon: <CarrierConfigIcon color="navigationIconDefault" />,
              url: ROUTES.HEALTH_PLAN_HUB,
              hide: !isPlansShowed || isSubgroup,
              options: [
                ...(isHealthPlansAccessible
                  ? [
                      {
                        key: 'health_plans',
                        title: 'Health Plans',
                        url: ROUTES.HEALTH_PLANS,
                      },
                    ]
                  : []),
                {
                  key: 'carrier_config',
                  title: 'Carrier Config',
                  url: ROUTES.CARRIER_CONFIG,
                },
                ...(isViewCopayGroups
                  ? [
                      {
                        key: 'copay_adjudication',
                        title: 'Copay Adjudication',
                        url: ROUTES.COPAY_ADJUDICATION,
                      },
                    ]
                  : []),
              ],
            },
          ]
        : []),
      {
        key: 'claims',
        title: 'Claims',
        icon: <ClaimsIcon color="navigationIconDefault" />,
        url: ROUTES.CLAIMS,
        subUrls: [ROUTES.CLAIM_PROCESS, ROUTES.MANAGE_CLAIMS_PROCESSORS],
        hide: !isClaimsButtonAvailable,
      },
      {
        key: 'folder',
        title: 'File Manager',
        icon: <FolderIcon color="navigationIconDefault" />,
        url: ROUTES.FILE_MANAGER,
        subUrls: [ROUTES.FILE_DETAILS],
        hide: isSubgroup || !isFileManagerAccessible || !isFileManagerEnabled,
      },

      {
        key: 'finOps',
        title: 'FinOps',
        icon: <ReconciliationReportsIcon color="navigationIconDefault" />,
        url: ROUTES.FINOPS,
        subUrls: [ROUTES.RECONCILIATION_REPORT],
        hide: !isFinOpsShowed,
      },
      {
        key: 'roleBuilder',
        title: 'Roles and Security',
        icon: <RolesAndSecurityIcon color="navigationIconDefault" />,
        url: ROUTES.ROLES_AND_SECURITY,
        subUrls: [ROUTES.ROLES_AND_SECURITY, ROUTES.ROLE_SETUP],
        hide: !isRolesAndSecurityAvailable || isObservingModeActive,
      },
      {
        key: 'reports',
        title: 'Reports',
        icon: <ReportsIcon color="navigationIconDefault" />,
        url: ROUTES.NEW_REPORTS,
        subUrls: [
          ROUTES.EMPLOYEE_DEMOGRAPHIC_REPORT,
          ROUTES.PARTICIPANT_ACTIVITY_REPORT,
          ROUTES.PAYROLL_CONTRIBUTION_REPORT,
          ROUTES.HEALTH_PLAN_ENROLLMENTS_REPORT,
          ROUTES.CLAIMS_ACTIVITY_REPORT,
          ROUTES.REPLENISHMENT_INVOICE_REPORT,
          ROUTES.CONTRIBUTION_INVOICE_REPORT,
          ROUTES.FORFEITURE_REPORT,
        ],
        // isNewBadge: true,
        hide: !isReportsAvailable,
      },
      {
        key: 'contributions',
        title: 'Contributions',
        icon: <ContributionsMenuIcon color="navigationIconDefault" />,
        url: ROUTES.CONTRIBUTIONS,
        hide: !isContributionsAccessible || !isContributionsAccessibleObserving,
      },
      {
        key: 'quickLinks',
        title: 'Quick Links',
        delimiter: true,
        icon: <QuickLinksIcon color="navigationIconDefault" />,
        // isDotsIcon: true,
        hide: isEmployer || isSubsidiary || isSubgroup,
      },
      {
        key: 'setupPartner',
        isLink: true,
        minify: true,
        title: 'Set up Partner',
        url: getPath(ROUTES.PARTNERS, { shouldOpenModal: true }),
        hide: !isSetUpPartnerOptionAccessible || !isSystem,
      },
      {
        key: 'setupDistributor',
        minify: true,
        isLink: true,
        title: 'Set up Distributor',
        url: getPath(ROUTES.DISTRIBUTORS, { shouldOpenModal: true }),
        hide: !isSetUpDistributorOptionAccessible || isDistributor || isEmployer || isSubsidiary,
      },
      {
        key: 'setupEmployer',
        minify: true,
        isLink: true,
        title: 'Set up Employer',
        url: getPath(ROUTES.EMPLOYERS, { shouldOpenModal: true }),
        hide: !isSetUpEmployerOptionAccessible || isEmployer || isSubgroup || isSubsidiary,
      },
    ],
    [
      isPartnersOptionAccessible,
      isSystem,
      isDistributor,
      isEmployer,
      isEmployee,
      isSubsidiary,
      isSubgroup,
      hasPermissionToViewProfiles,
      isEmployersOptionAccessible,
      isPlansShowed,
      isProcessingDefinitionAccessible,
      isServicesAccessible,
      isPayoutDefinitionsAccessible,
      isHealthPlanHubAccessible,
      isHealthPlansAccessible,
      isViewCopayGroups,
      isClaimsButtonAvailable,
      isFileManagerAccessible,
      isFileManagerEnabled,
      isFinOpsShowed,
      isRolesAndSecurityAvailable,
      isObservingModeActive,
      isReportsAvailable,
      isContributionsAccessible,
      isContributionsAccessibleObserving,
      getPath,
      isSetUpPartnerOptionAccessible,
      isSetUpDistributorOptionAccessible,
      isSetUpEmployerOptionAccessible,
    ]
  );

  const filteredOptions = useMemo(() => allOptions.filter((item) => !item.hide), [allOptions]);
  if (!auth || !user) return [];

  return filteredOptions;
};
